import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { IMarketingCampaignGuardrailsModel } from '../marketing/marketing-campaign-guardrails.model';
import { AutomationBatchEntity } from './automation-batch.entity';
import { AutomationFlowEntity } from './automation-flow.entity';
import { AutomationTargetListEntity } from './automation-target-list.entity';
import { BaseEntity } from './base.entity';
import { EmailTemplateEntity } from './email-template.entity';

/** Lifecycle of a managed marketing campaign, as the setup screen shows it. */
export type MarketingCampaignStatus =
	| 'draft'
	| 'active'
	| 'paused'
	| 'archived';

/**
 * Where a campaign's recipients come from.
 *
 * - `play`: a parameterized target list provider (`@TargetListProvider`) that
 *   opted in with `marketingPlay = true`.
 * - `saved_search`: an existing `list_saved_search` row.
 * - `static_list`: an existing static `_automation_target_list` row.
 */
export type MarketingCampaignAudienceSourceType =
	| 'play'
	| 'saved_search'
	| 'static_list';

/**
 * A managed marketing campaign.
 *
 * This row is DELIBERATELY THIN. It holds the user's declarations and nothing
 * else. Everything about how a campaign is running — schedule, run status,
 * counts, next run, last run — lives on the compiled automation batch and is
 * read through it. Duplicating any of that here would create two sources of
 * truth that drift the first time the engine runs without going through this
 * module.
 *
 * The three FK columns point at the compiled engine artifacts. They are outputs
 * of `MarketingCampaignService.compile`, not user input: a save regenerates
 * them from the declarations above.
 *
 * DEPENDENCY NOTE: a `play` campaign owns an `AutomationTargetListEntity` with
 * `listType = 'dynamic'`, `entityType = <play key>` and `query = <play knobs>`.
 * That entity comments those three fields as legacy leftovers of a removed
 * dynamic-list feature. They are NOT dead — managed campaigns depend on them,
 * and `AutomationTargetListService.resolveTargetList` materializes the provider
 * results through them before every run. Do not drop those columns.
 */
@Entity('marketing_campaign')
export class MarketingCampaignEntity extends BaseEntity {
	@Column({ type: 'varchar', length: 255 })
	public name!: string;

	@Column({
		type: 'enum',
		enum: ['draft', 'active', 'paused', 'archived'],
		default: "'draft'",
	})
	public status!: MarketingCampaignStatus;

	// === Declaration: audience ===

	@Column({
		type: 'enum',
		enum: ['play', 'saved_search', 'static_list'],
	})
	public audienceSourceType!: MarketingCampaignAudienceSourceType;

	/**
	 * Identifies the audience within its source type.
	 *
	 * For `play` this is the provider's `entityType` (e.g. `due-to-reorder`).
	 * For `saved_search` and `static_list` it is the numeric row id, stored as a
	 * string so one column serves all three source types.
	 */
	@Column({ type: 'varchar', length: 255 })
	public audienceSourceKey!: string;

	/**
	 * Play knobs, in the scale the PROVIDER expects — not the scale the UI shows.
	 * Notably `minLikelihood` is a 0–1 fraction here; the setup screen presents
	 * it as a whole-number percent and the service divides by 100 on the way in.
	 */
	@Column({ type: 'json', nullable: true })
	public audienceParams?: Record<string, unknown> | null;

	// === Declaration: email ===

	@Column({
		name: 'fk_email_template__id',
		type: 'int',
		unsigned: true,
	})
	public fkEmailTemplateId!: number;

	/**
	 * Subject line for the send.
	 *
	 * `email_template` has no subject column — the send_email step takes the
	 * subject from its own config — so the campaign owns it. Merge fields from
	 * the audience output schema are allowed (`{{entity.name}}`).
	 */
	@Column({ type: 'varchar', length: 500 })
	public subject!: string;

	// === Declaration: guardrails and attribution ===

	@Column({ type: 'json', nullable: true })
	public guardrails?: IMarketingCampaignGuardrailsModel | null;

	/**
	 * Days after a send during which an order from a recipient counts as
	 * attributed to this campaign.
	 */
	@Column({ type: 'int', unsigned: true, default: 14 })
	public attributionWindowDays!: number;

	// === Compiled engine artifacts (outputs of compile, never user input) ===

	@Column({
		name: 'fk__automation_flow__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public fkAutomationFlowId?: number | null;

	@Column({
		name: 'fk__automation_batch__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public fkAutomationBatchId?: number | null;

	@Column({
		name: 'fk__automation_target_list__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public fkAutomationTargetListId?: number | null;

	// === Relations ===

	@ManyToOne(() => EmailTemplateEntity)
	@JoinColumn({ name: 'fk_email_template__id' })
	public emailTemplate?: EmailTemplateEntity;

	@ManyToOne(() => AutomationFlowEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_flow__id' })
	public automationFlow?: AutomationFlowEntity;

	@ManyToOne(() => AutomationBatchEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_batch__id' })
	public automationBatch?: AutomationBatchEntity;

	@ManyToOne(() => AutomationTargetListEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_target_list__id' })
	public automationTargetList?: AutomationTargetListEntity;

	// === Indexes ===

	@Index('idx_marketing_campaign_status')
	public static statusIndex: [string] = ['status'];

	@Index('idx_marketing_campaign_template')
	public static templateIndex: [string] = ['fkEmailTemplateId'];

	@Index('idx_marketing_campaign_flow')
	public static flowIndex: [string] = ['fkAutomationFlowId'];

	@Index('idx_marketing_campaign_batch')
	public static batchIndex: [string] = ['fkAutomationBatchId'];

	@Index('idx_marketing_campaign_target_list')
	public static targetListIndex: [string] = ['fkAutomationTargetListId'];
}
